import { z } from 'zod';

import {
  CardCompanySnapshotSchema,
  CardCustomerSnapshotSchema,
  CardIdSchema,
  CardOriginChannelSchema,
} from '../card/card';
import { PrioritySchema } from '../card/priority';

import { TaskSchema } from './task';

/**
 * Wire-only projection of the parent card embedded alongside each
 * `Task` on the task board. Mirrors
 * `Pick<Card, 'id' | 'seq' | 'customer' | 'company' | 'priority' | 'originChannel'>`
 * — the surfaceable fields a task cell needs to render the parent
 * card preview without forcing the client to fetch the full card.
 *
 * Defined in `@voll-intelligence/domain` (not co-located with the
 * board repo) so backend route responses and frontend repository
 * parsing share a single contract.
 */
export const TaskCardCellSchema = z.object({
  id: CardIdSchema,
  seq: z.number().int().min(1),
  customer: CardCustomerSnapshotSchema,
  company: CardCompanySnapshotSchema,
  priority: PrioritySchema,
  originChannel: CardOriginChannelSchema,
});

export type TaskCardCell = z.infer<typeof TaskCardCellSchema>;

/**
 * Task board row: every `Task` carries a denormalized `card` cell so
 * the UI can render the parent card preview inline, plus the resolved
 * display name of its assignee (null when unassigned) and whether the
 * requesting user is the task's human assignee.
 */
export const TaskWithCardSchema = TaskSchema.and(
  // `agentDisplayName` + `assignedToMe` tolerant (defaults) so a response
  // without them still parses — only the card-tasks endpoint sets
  // `assignedToMe`; the board endpoints leave it `false`.
  z.object({
    card: TaskCardCellSchema,
    agentDisplayName: z.string().nullable().default(null),
    assignedToMe: z.boolean().default(false),
  }),
);

export type TaskWithCard = z.infer<typeof TaskWithCardSchema>;
